/* REACT PACKAGE IMPORTS */
import { useEffect, useState, useRef, Fragment } from "react";
import { Container } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

/* MUI PACKAGE IMPORTS */
import Chip from "@mui/material/Chip";
import PendingIcon from "@mui/icons-material/Pending";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import UpdateIcon from "@mui/icons-material/Update";
import CancelIcon from "@mui/icons-material/Cancel";
import { ThemeProvider } from "@mui/material/styles";

/* INTERNAL IMPORTS */
import { theme } from "../styles/StyledComponents";
import {
  LocalDateTime,
  UTCDateTime,
  formatDate,
} from "../Components/DateTimeFormatter";
import Header from "../Components/Header";
import Loading from "../Components/Loading";

import { openModal } from "../Features/Errors/Slices/errorSlice";
import { setIsLoading } from "../Features/Auth/Slices/authSlice";
import { fetchCandidateSessions } from "../Features/Sessions/Actions/SessionsActions";
import { userLogged } from "../Features/Auth/Slices/authSlice";

import "../styles/Navbar.css";

export default function SessionsCandidate({ paragraph = "", title = "" }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading } = useSelector((store) => store.auth);
  const user = useSelector(userLogged);

  const [displaySessions, setDisplaySessions] = useState([]);

  const mounted = useRef(null);

  const formatDateTimes = (datetime) => {
    if (!datetime) return "Time and Date has not been set";

    try {
      const localDateTime = LocalDateTime(datetime);
      const utcDateTime = UTCDateTime(datetime);

      if (
        typeof localDateTime !== "string" ||
        typeof utcDateTime !== "string"
      ) {
        throw new Error("DateTime functions must return strings");
      }

      const [_, localTimeFull] = localDateTime.split(",");
      const [utcDate, utcTimeFull] = utcDateTime.split(",");

      const localTime = localTimeFull?.substring(0, 6) ?? "Invalid";
      const utcTime = utcTimeFull?.substring(0, 6) ?? "Invalid";
      const date = utcDate ?? "Invalid";

      return (
        <div className="col-span-2 flex flex-col flex-nowrap">
          <p>Date - {date}</p>
          <p>Local - {localTime}</p>
          <p>UTC - {utcTime}</p>
        </div>
      );
    } catch (error) {
      console.error("Error formatting date/time:", error);
      throw new Error("Error parsing date/time");
    }
  };

  const formatSessionStatus = (sessionStatus) => {
    let status = "Unknown";
    let color = "default";
    let icon = null;

    try {
      if (!sessionStatus || typeof sessionStatus !== "string") {
        throw new Error("Invalid session status");
      }

      const normalizedStatus = sessionStatus.trim().toLowerCase();

      switch (normalizedStatus) {
        case "pending":
          status = "Ready";
          color = "info";
          icon = <PendingIcon />;
          break;
        case "started":
          status = "Started";
          color = "primary";
          icon = <PlayCircleFilledIcon />;
          break;
        case "future":
          status = "Upcoming";
          color = "secondary";
          icon = <UpdateIcon />;
          break;
        case "expired":
          status = "Expired";
          color = "error";
          icon = <CancelIcon />;
          break;
        case "finished":
          status = "Finished";
          color = "success";
          icon = <CheckCircleIcon />;
          break;
        default:
          status = "Unknown";
          color = "default";
          icon = null;
          break;
      }
    } catch (error) {
      console.error("Error formatting session status:", error);
      status = "Error";
      color = "error";
      icon = <CancelIcon />;
    }

    return (
      <ThemeProvider theme={theme}>
        <Chip label={status} color={color} size="small" icon={icon} />
      </ThemeProvider>
    );
  };

  const handleNavigation = (pathname, id) => {
    navigate(`${pathname}/${id}`);
  };

  useEffect(() => {
    mounted.current = true;
    if (mounted.current) dispatch(setIsLoading(true));
    return () => (mounted.current = false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    if (!isLoading) {
      sessionStorage.removeItem("scheduleStatus");
      sessionStorage.removeItem("sessionStatus");
      sessionStorage.removeItem("pages");
      return;
    }
    if (mounted.current) {
      document.title = "Sessions";
      const date = new Date(Date.now());
      const dateUTC = formatDate(date);
      const userid = user.userid;
      const requestBody = { userId: userid, startDate: dateUTC };

      dispatch(fetchCandidateSessions(requestBody)).then((response) => {
        if (
          response.payload.length > 0 &&
          typeof response.payload[0] === "object"
        ) {
          // const filterSessionsId = [8, 18]
          // setDisplaySessions(response.payload.filter((session) => filterSessionsId.includes(session?.schedule?.exam?.exid)));
          setDisplaySessions(response.payload);
        }

        dispatch(setIsLoading(false));
      });
    }
    return () => (mounted.current = false);
  }, [isLoading, user.userid, dispatch]);

  // Poll whether the session is ready to start
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setDisplaySessions((prev) =>
        prev.map((session) => {
          const start = new Date(session.schedule?.starttime);
          if (session.status === "future" && start <= now) {
            dispatch(
              openModal({
                message: "The session is ready now",
                severity: "info",
              }),
            );
            return { ...session, status: "Ready" };
          }
          return session;
        }),
      );
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const supportNavigation = () => {
    return (
      <section className="card p-8 my-4">
        <h1 className="text-xl">
          Can't find your session? Contact us through support
        </h1>
        <p>
          Click{" "}
          <span>
            <a
              href="https://support.victvs.com/category/knowledge-base/fifa/"
              className="underline"
            >
              here
            </a>
          </span>{" "}
          to redirect to the support site
        </p>
        <div className="my-3">
          <p>
            Your Full Name:<span className="fw-bold"> {user?.fullname}</span>
          </p>
          <p>
            Your FIFA VX Username:{" "}
            <span className="fw-bold">{user?.username}</span>
          </p>
        </div>
      </section>
    );
  };

  return (
    <Container className="py-4 position-relative w-[90%]">
      <Header paragraph={paragraph} title={title}>
        <p className="font-semibold text-3xl mt-2">My assessments</p>
      </Header>
      {(displaySessions.length > 0) ? (
        <ul
          className="my-3 box background-secondary"
          style={{ width: "100%" }}
          aria-busy
        >
          {displaySessions.map(({ schedule, esuid, status }, i) => {
            const {
              referencetitle = "",
              title = "",
              starttime = "",
              exam = {},
            } = schedule || {};

            return (
              <Fragment key={i}>
                {i === 0 && (
                  <li className="grid grid-cols-10 gap-3 items-center p-4 text-sm font-semibold border-b-2">
                    <p className="col-span-3">Reference</p>
                    <p className="col-span-3">Title</p>
                    <p className="col-span-2">Start time</p>
                    <p className="">Time limit</p>
                    <p className="">Status</p>
                  </li>
                )}
                <li
                  onClick={() => {
                    const pathname = window.location.pathname;
                    return handleNavigation(pathname, esuid);
                  }}
                  className={`grid grid-cols-10 gap-3 cursor-pointer items-center ${
                    i % 2 !== 0 ? "" : "bg-[#0000000a]"
                  } p-4 text-sm`}
                >
                  <p className={"col-span-3"}>{referencetitle}</p>
                  <p className={"col-span-3 font-semibold"}>{title}</p>
                  <div className={"col-span-2 w-auto"}>
                    {starttime ? formatDateTimes(starttime) : ""}
                  </div>
                  <p className={""}>{exam?.duration?.stringshort}</p>
                  <div className={"w-auto"}>
                    {status ? formatSessionStatus(status) : ""}
                  </div>
                </li>
              </Fragment>
            );
          })}
        </ul>
      ) : (
        supportNavigation()
      )}
    </Container>
  );
}
